(() => {
  'use strict';

  if (window.PWCartNoRefreshLoaded) return;
  window.PWCartNoRefreshLoaded = true;

  let suppressUntil = 0;
  let expiryTimer = null;
  let pendingView = '';
  let renderTimer = null;

  const rootPath = () => (
    window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/'
  );

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const properties = (item) => (item && item.properties) || {};
  const titleOf = (item) => String(
    item && (item.product_title || item.title) || ''
  ).trim();
  const linePrice = (item) => Number(
    item && (
      item.final_line_price !== undefined
        ? item.final_line_price
        : item.line_price || 0
    ) || 0
  );

  const isService = (item) => {
    const props = properties(item);
    return normalize(props['_Paulissen type']) === 'service'
      || normalize(item && item.product_type).includes('extra dienst');
  };

  const bundleOf = (item) => {
    const bundle = String(properties(item)['_Paulissen bundel'] || '').trim();
    return bundle || `legacy-${String(item && item.key || '')}`;
  };

  const serviceGroup = (item) => normalize(properties(item)._Servicegroep);

  const money = (cents) => {
    const amount = Number(cents || 0) / 100;
    if (Math.abs(amount - Math.round(amount)) < 0.00001) {
      return `€${Math.round(amount).toLocaleString('nl-NL')},-`;
    }
    return `€${amount.toLocaleString('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const armNoRefresh = (view = '') => {
    suppressUntil = Date.now() + 15000;
    window.PWCartNoRefreshPending = true;
    if (view) pendingView = view;

    if (expiryTimer) window.clearTimeout(expiryTimer);
    expiryTimer = window.setTimeout(() => {
      suppressUntil = 0;
      window.PWCartNoRefreshPending = false;
      pendingView = '';
    }, 15000);
  };

  const consumeNoRefresh = () => {
    const active = window.PWCartNoRefreshPending && Date.now() < suppressUntil;
    if (!active) return false;

    suppressUntil = 0;
    window.PWCartNoRefreshPending = false;
    if (expiryTimer) window.clearTimeout(expiryTimer);
    expiryTimer = null;
    return true;
  };

  window.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest(
      '.pw-service-input, [data-pw-service-level-input], [data-pw-return-quantity]'
    )) {
      armNoRefresh();
    }
  }, true);

  window.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-pw-confirm-options]')) {
      armNoRefresh('summary');
      return;
    }

    if (target.closest('[data-pw-edit-options]')) {
      armNoRefresh('options');
      return;
    }

    if (target.closest('[data-pw-remove-service]')) {
      armNoRefresh();
    }
  }, true);

  const wrapRefresh = (element) => {
    if (!element || element.__pwNoRefreshWrapped || typeof element.refresh !== 'function') return;

    const originalRefresh = element.refresh.bind(element);
    element.__pwNoRefreshWrapped = true;

    element.refresh = async function pwRefreshWrapper(...args) {
      if (consumeNoRefresh()) {
        return this;
      }
      return originalRefresh(...args);
    };
  };

  const install = () => {
    document.querySelectorAll('.cc-main-cart cart-items, cart-drawer').forEach(wrapRefresh);
  };

  const createRemoveButton = (attributes, label) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = '×';
    button.setAttribute('aria-label', label);
    Object.entries(attributes).forEach(([name, value]) => {
      button.setAttribute(name, String(value || ''));
    });
    return button;
  };

  const renderSummary = async () => {
    const step = document.querySelector('.pw-cart-summary-step');
    if (!step) return;

    const response = await fetch(`${rootPath()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) return;

    const cart = await response.json();
    const mainItems = [];
    const servicesByBundle = new Map();
    const generalServices = [];

    (cart.items || []).forEach((item) => {
      if (!isService(item)) {
        mainItems.push(item);
        return;
      }

      if (serviceGroup(item) === 'recycling') {
        generalServices.push(item);
        return;
      }

      const bundle = String(properties(item)['_Paulissen bundel'] || '').trim();
      if (!bundle) return;
      if (!servicesByBundle.has(bundle)) servicesByBundle.set(bundle, []);
      servicesByBundle.get(bundle).push(item);
    });

    let level = step.querySelector('.pw-summary-level');
    const levelValue = normalize(cart.attributes && cart.attributes.pw_service_level);
    const levelLabels = {
      threshold: 'Drempellevering',
      standard_installation: 'Bezorging + standaard installatie'
    };

    if (levelValue && levelLabels[levelValue]) {
      if (!level) {
        level = document.createElement('p');
        level.className = 'pw-summary-level';
        const heading = step.querySelector('h2');
        if (heading) heading.insertAdjacentElement('afterend', level);
      }
      level.textContent = '';
      const strong = document.createElement('strong');
      strong.textContent = 'Bezorg- en installatiemethode: ';
      level.append(strong, document.createTextNode(levelLabels[levelValue]));
      level.hidden = false;
    } else if (level) {
      level.hidden = true;
    }

    const productsRoot = step.querySelector('.pw-summary-products');
    if (productsRoot) {
      productsRoot.textContent = '';

      mainItems.forEach((item) => {
        const bundle = bundleOf(item);
        const linkedServices = servicesByBundle.get(bundle) || [];
        const subtotal = linkedServices.reduce(
          (sum, service) => sum + linePrice(service),
          linePrice(item)
        );

        const article = document.createElement('article');
        article.className = 'pw-summary-product';

        const main = document.createElement('div');
        main.className = 'pw-summary-product__main';

        const price = document.createElement('span');
        price.className = 'pw-summary-product__price';
        price.textContent = `Subtotaal ${money(subtotal)}`;

        const heading = document.createElement('h3');
        heading.textContent = titleOf(item);

        const quantity = document.createElement('span');
        quantity.textContent = `Aantal: ${Math.max(1, Number(item.quantity || 1))}`;

        const removeProduct = createRemoveButton({
          'data-pw-remove-summary-product': '',
          'data-line-key': item.key,
          'data-pw-bundle': bundle,
          'data-pw-product-title': titleOf(item)
        }, `${titleOf(item)} verwijderen`);

        main.append(price, heading, quantity, removeProduct);

        const list = document.createElement('ul');
        list.className = 'pw-summary-product__services';
        list.setAttribute('role', 'list');

        if (!linkedServices.length) {
          const empty = document.createElement('li');
          empty.className = 'pw-summary-product__empty';
          empty.textContent = 'Geen extra diensten gekozen';
          list.appendChild(empty);
        } else {
          linkedServices.forEach((service) => {
            const row = document.createElement('li');
            const serviceTitle = document.createElement('span');
            const servicePrice = document.createElement('span');
            serviceTitle.textContent = titleOf(service);
            servicePrice.textContent = money(linePrice(service));

            const removeService = createRemoveButton({
              'data-pw-remove-service': '',
              'data-line-key': service.key,
              'data-pw-bundle': bundle,
              'data-pw-service-title': titleOf(service)
            }, `${titleOf(service)} verwijderen`);

            row.append(serviceTitle, servicePrice, removeService);
            list.appendChild(row);
          });
        }

        article.append(main, list);
        productsRoot.appendChild(article);
      });
    }

    const oldGeneral = step.querySelector('.pw-summary-general');
    if (oldGeneral) oldGeneral.remove();

    if (generalServices.length) {
      const section = document.createElement('section');
      section.className = 'pw-summary-general';
      const heading = document.createElement('h3');
      heading.textContent = 'Algemene bezorgafspraken';
      const list = document.createElement('ul');
      list.setAttribute('role', 'list');

      generalServices.forEach((service) => {
        const row = document.createElement('li');
        const serviceTitle = document.createElement('span');
        const servicePrice = document.createElement('span');
        serviceTitle.textContent = `${Math.max(1, Number(service.quantity || 1))}× Oude apparaat gratis retour`;
        servicePrice.textContent = 'Gratis';
        const removeService = createRemoveButton({
          'data-pw-remove-service': '',
          'data-line-key': service.key,
          'data-pw-bundle': 'algemeen',
          'data-pw-service-title': 'Oude apparaat retour'
        }, 'Oude apparaat retour verwijderen');
        row.append(serviceTitle, servicePrice, removeService);
        list.appendChild(row);
      });

      section.append(heading, list);
      const note = step.querySelector('.pw-summary-note');
      if (note) note.insertAdjacentElement('beforebegin', section);
    }

    const total = step.querySelector('.pw-summary-total strong');
    if (total) total.textContent = money(cart.total_price);
  };

  const switchView = (view) => {
    const options = document.querySelector('[data-pw-cart-step="options"]');
    const summary = document.querySelector('[data-pw-cart-step="summary"]');
    if (!options || !summary) return;

    options.hidden = view !== 'options';
    summary.hidden = view !== 'summary';

    const target = view === 'summary' ? summary : options;
    target.scrollIntoView({ behavior: 'auto', block: 'start' });
  };

  const reconcileVisibleCart = async () => {
    const summary = document.querySelector('[data-pw-cart-step="summary"]');
    const shouldRenderSummary = pendingView === 'summary'
      || (summary && !summary.hidden);

    if (shouldRenderSummary) {
      try {
        await renderSummary();
      } catch (error) {
        console.warn('[PW cart] Samenvatting kon niet zonder refresh worden bijgewerkt.', error);
      }
    }

    if (pendingView) {
      const view = pendingView;
      pendingView = '';
      switchView(view);
    }
  };

  document.addEventListener('on:cart:change', () => {
    if (renderTimer) window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(reconcileVisibleCart, 80);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', install, { once: true });
  } else {
    install();
  }

  let attempts = 0;
  const retryInstall = () => {
    install();
    attempts += 1;
    if (attempts < 50) window.setTimeout(retryInstall, 100);
  };
  retryInstall();

  new MutationObserver(install).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
